import VirtualKeyboard from '@components/ui/virtual-keyboard';
import { EventHandlerPayload, LiveChatWidget } from '@livechat/widget-react';
import { Box, Stack, useMediaQuery } from '@mui/material';
import { motion } from 'framer-motion';
import {
  createContext,
  memo,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { Outlet } from 'react-router-dom';
import { getColor } from '../../assets/theme';
import Help from '../../components/ui/help';
import { guideTitleType, setGuideState } from '../../store/slices/guide.slice';
import ComingSoon from '../partials/coming-soon';
import Header from '../partials/header';
import PageLoad from '../partials/page-load';
import Sidebar from '../partials/sidebar';
import LicenseWarning from '../partials/license-warning';
import ACCGAccountPeriodWarning from '@shared/partials/account-period-warning';

export type LayoutContextType = {
  openSidebar: () => void;
  closeSidebar: () => void;
  toggleSidebar: () => void;
  isSidebarOpen: boolean;
  openModal: () => void;
  closeModal: () => void;
  toggleModal: () => void;
  isOpenModal: boolean;
  openComingSoon: () => void;
  closeComingSoon: () => void;
  toggleComingSoon: () => void;
  isOpenComingSoon: boolean;
  openPageLoad: () => void;
  closePageLoad: () => void;
  togglePageLoad: () => void;
  isOpenPageLoad: boolean;
  openModuleGuide: guideTitleType | null | undefined;
  handleOpenModuleGuide: (module: guideTitleType) => void;
  virtualKeyboard?: boolean;
};

export const LayoutContext = createContext<LayoutContextType>({
  openSidebar: () => {},
  closeSidebar: () => {},
  toggleSidebar: () => {},
  isSidebarOpen: false,
  openModal: () => {},
  closeModal: () => {},
  toggleModal: () => {},
  isOpenModal: false,
  openComingSoon: () => {},
  closeComingSoon: () => {},
  toggleComingSoon: () => {},
  isOpenComingSoon: false,
  openPageLoad: () => {},
  closePageLoad: () => {},
  togglePageLoad: () => {},
  isOpenPageLoad: false,
  handleOpenModuleGuide: () => {},
  openModuleGuide: undefined,
  virtualKeyboard: undefined,
});

const LiveChat = memo(() => {
  const handleNewEvent = (event: EventHandlerPayload<'onNewEvent'>) => {
    console.log('LiveChatWidget.onNewEvent', event);
  };

  return (
    <LiveChatWidget
      license='13948944'
      visibility='minimized'
      onNewEvent={handleNewEvent}
      group='0'
    />
  );
});

const MemoizedLayoutContextProvider = memo(LayoutContext.Provider);

const MainLayout = memo(() => {
  const { bgColor } = getColor();
  const constraintsRef = useRef(null);

  return (
    <Stack
      direction='column'
      sx={{
        height: '100%',
        width: '100%',
        overflow: 'hidden',
        // position: 'relative',
        flex: 1,
        minHeight: 0,
      }}
    >
      <Header />
      <Stack
        component={motion.div}
        ref={constraintsRef}
        sx={{
          height: '100%',
          bgcolor: bgColor,
          px: 3,
          py: 2,
          overflowY: 'auto',
          flex: 1,
          minHeight: 0,
        }}
      >
        <Outlet />
      </Stack>
      <LiveChat />
      <VirtualKeyboard {...{ constraintsRef }} />
    </Stack>
  );
});

const Layout = ({ virtualKeyboard }: { virtualKeyboard?: boolean }) => {
  // The purpose of the layout partial is to set some common shared layout between modules. This can range from UI components such as the header and the footer to actions such using useEffect.

  const isSmallScreen = useMediaQuery('(max-width:425px)');

  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [isOpenComingSoon, setIsOpenComingSoon] = useState(false);
  const [isOpenPageLoad, setIsOpenPageLoad] = useState(false);
  const [openModuleGuide, setOpenModuleGuide] = useState<
    guideTitleType | null | undefined
  >();
  const dispatch = useDispatch();

  const handleOpenModuleGuide = (module: guideTitleType) => {
    setOpenModuleGuide(module);
    module && dispatch(setGuideState({ [module]: false }));
  };
  const openSidebar = () => setIsSidebarOpen(true);
  const closeSidebar = () => setIsSidebarOpen(false);
  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  const openModal = () => setIsOpenModal(true);
  const closeModal = () => setIsOpenModal(false);
  const toggleModal = () => setIsOpenModal(!isOpenModal);

  const openComingSoon = () => setIsOpenComingSoon(true);
  const closeComingSoon = () => setIsOpenComingSoon(false);
  const toggleComingSoon = () => setIsOpenComingSoon(!isOpenComingSoon);

  const openPageLoad = () => setIsOpenPageLoad(true);
  const closePageLoad = () => setIsOpenPageLoad(false);
  const togglePageLoad = () => setIsOpenPageLoad(!isOpenPageLoad);

  useEffect(() => {
    if (!isSmallScreen) return;
    setIsSidebarOpen(false);
  }, [isSmallScreen]);

  const memoizedValues = useMemo(
    () => ({
      openSidebar,
      openModuleGuide,
      handleOpenModuleGuide,
      closeSidebar,
      toggleSidebar,
      isSidebarOpen,
      openModal,
      closeModal,
      toggleModal,
      isOpenModal,
      openComingSoon,
      closeComingSoon,
      toggleComingSoon,
      isOpenComingSoon,
      openPageLoad,
      closePageLoad,
      togglePageLoad,
      isOpenPageLoad,
      virtualKeyboard,
    }),
    [
      isSidebarOpen,
      isOpenModal,
      isOpenComingSoon,
      isOpenPageLoad,
      openModuleGuide,
      virtualKeyboard,
    ],
  );

  return (
    <Stack sx={{ flex: 1, minHeight: 0 }}>
      <LicenseWarning />
      <ACCGAccountPeriodWarning />
      <Box
        sx={{
          display: 'flex',
          height: '100%',
          width: '100%',
          flex: 1,
          minHeight: 0,
        }}
      >
        <MemoizedLayoutContextProvider value={memoizedValues}>
          <Sidebar />
          <PageLoad />
          <MainLayout />
          <ComingSoon />
          <Help />
        </MemoizedLayoutContextProvider>
      </Box>
    </Stack>
  );
};

export default Layout;
